
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnChanges,
  OnInit, SimpleChanges
} from '@angular/core';
import { filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { FieldStatus, GetEntityById2GQL, PhoneDetails, PhoneDetailsFragment } from 'generated/graphql';

import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ToastrService } from 'core/toastr.service';
import { ContactTrackingDialog } from 'app/contact-tracking/dialogs/contact-tracking/contact-tracking.dialog';
import { MatDialog } from '@angular/material/dialog';
import { GetCustomFormService } from 'app/custom-forms/GetCustomForm.service';
import { from } from 'rxjs';

type Popovers = 'email' | 'call' | 'message' | 'address';

@Component({
  selector: 'app-entity-contact-buttons',
  templateUrl: './entity-contact-buttons.component.html',
  styleUrls: [`./entity-contact-buttons.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntityContactButtonsComponent extends WithDisposableSubject(WithChangesSubject(class { })) implements OnInit, OnChanges {
  @Input() entityId: string;
  @Input() campaignId: string;

  /** Should log conversation modal get loaded after user clicks contact method */
  @Input() opensLogContactModal: boolean = true;

  /** If contract trackig is not set up, don't show the dialog */
  contactTrackingEnabled = false;

  bestAddress: any;
  bestPhone: any;
  bestEmail: any;

  visiblePopover: Popovers;

  // Handles clicks outside of the popovers
  // and close it if it is open
  @HostListener('document:click', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  handleOutsideClick(event: MouseEvent | TouchEvent) {
    if (this.visiblePopover && !this.elRef.nativeElement.contains(event.target)) {
      this.visiblePopover = null;
    }
  }

  constructor(
    private elRef: ElementRef,
    private toastrService: ToastrService,
    private _getEntityGQL: GetEntityById2GQL,
    private _getCustomFormSvc: GetCustomFormService,
    private _cd: ChangeDetectorRef,
    private _dialog: MatDialog
  ) {
    super();
  }

  ngOnInit(): void {
    this.watchForChangeKeys('campaignId', 'entityId').pipe(
      startWith<any, any>(null),
      takeUntil(this.disposer),
      map(() => ({ entityId: this.entityId, campaignId: this.campaignId })),
      filter(({ entityId, campaignId }) => !!entityId && !!campaignId),
      switchMap(({ entityId, campaignId }) => this._getEntityGQL.watch({
        entityId,
        campaignId,
        withToplines: false,
        withBestAddress: true,
        withBestPhone: true,
        withBestEmail: true
      }).valueChanges.pipe(takeUntil(this.disposer))),
      map(({ data }) => data.getEntity),
      switchMap(entity => from(
        this._getCustomFormSvc.contactFormEnabled(entity.entityTypeId, entity.campaignContextId)
        ).pipe(
          map((contactTrackingEnabled) => ({ entity, contactTrackingEnabled })
        ))
      )
    ).subscribe(
      ({contactTrackingEnabled, entity}) => {
        this.contactTrackingEnabled = contactTrackingEnabled
        this.bestAddress = entity.entityType.addressEnabled ? entity.bestAddress : null;
        this.bestPhone = entity.entityType.phoneNumberEnabled ? entity.bestPhoneNumber : null;
        this.bestEmail = entity.entityType.emailEnabled ? entity.bestEmail : null;
        this._cd.markForCheck();
      }
    );

  }

  loadLogConversationModal(contactItem: any) {
    if (!this.contactTrackingEnabled) {
      return;
    }
    if (!this.opensLogContactModal) {
      return;
    }

    ContactTrackingDialog.open(this._dialog, {
      data: {
        entityId: this.entityId,
        campaignId: this.campaignId,
        contactItem
      }
    })
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    super.ngOnChanges(simpleChanges);
  }

  sendSMS(phone: PhoneDetailsFragment) {
    window.open(`sms:${phone.number}`, '_self');
    this.loadLogConversationModal(phone);
  }

  isDoNotContact(item: { status: FieldStatus }) {
    return item?.status === FieldStatus.DoNotContact;
  }

  togglePopover(popover?: Popovers) {
    this.visiblePopover = (this.visiblePopover === popover) ? null : popover;
    this._cd.markForCheck();
  }

  copied() {
    this.toastrService.success('Copied to clipboard');
  }
}
